import React, { useEffect, useRef } from 'react';

declare const Plyr: any;

interface MediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
  type: 'audio' | 'video' | 'youtube';
  youtubeId?: string;
}

const MediaModal: React.FC<MediaModalProps> = ({ isOpen, onClose, title, url, type, youtubeId }) => {
  const playerRef = useRef<any>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof Plyr === 'undefined') return;

    const timeout = setTimeout(() => {
      if (!elementRef.current) return;
      
      const mediaElement = elementRef.current.querySelector('.modal-plyr-player');
      if (!mediaElement) return;

      const videoConfig = {
        controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
        tooltips: { controls: true, seek: true },
        autoplay: true,
        origin: window.location.origin,
        youtube: { noCookie: true, rel: 0, modestbranding: 1, origin: window.location.origin }
      };

      const audioConfig = {
        controls: ['play', 'progress', 'current-time', 'mute', 'volume'],
        tooltips: { controls: true, seek: true },
        autoplay: true,
        origin: window.location.origin
      };

      try {
        const player = new Plyr(mediaElement, type === 'audio' ? audioConfig : videoConfig);
        
        playerRef.current = player;

        // Forza il play dopo un istante per superare eventuali blocchi browser
        player.on('ready', () => {
          player.play().catch(() => {
            console.log("Autoplay blocked by browser, user interaction required.");
          });
        });
      } catch (err) {
        console.error('Modal Plyr Error:', err);
      }
    }, 150);

    return () => {
      clearTimeout(timeout);
      if (playerRef.current && typeof playerRef.current.destroy === 'function') {
        try {
          playerRef.current.destroy();
          playerRef.current = null;
        } catch (e) {}
      }
    };
  }, [isOpen, type, url]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-8 pt-24 md:pt-32 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-midnight/90 backdrop-blur-md animate-fadeIn" 
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative z-10 w-full max-w-4xl bg-midnight border border-gold/30 shadow-[0_0_50px_rgba(212,175,55,0.2)] animate-scaleIn overflow-hidden rounded-sm mb-12">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gold/10 bg-white/5">
          <div>
            <h3 className="text-xl font-serif text-gold">{title}</h3>
            <p className="text-[10px] text-gold/60 uppercase tracking-widest">
              {type === 'audio' ? 'Registrazione Audio' : 'Esecuzione Video'}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gold/60 hover:text-gold transition-colors p-2"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Player Area */}
        <div className="p-4 md:p-8" ref={elementRef}>
          {type === 'audio' ? (
            <div className="py-12">
              <audio className="modal-plyr-player" controls>
                <source src={url} type="audio/mp3" />
              </audio>
            </div>
          ) : (
            <div className="aspect-video bg-black rounded-sm relative">
              {type === 'youtube' ? (
                <div className="modal-plyr-player" data-plyr-provider="youtube" data-plyr-embed-id={youtubeId}></div>
              ) : (
                <video className="modal-plyr-player" playsInline controls preload="auto">
                  <source src={url} type="video/mp4" />
                </video>
              )}
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-4 bg-gold/5 text-center">
          <p className="text-[11px] text-gold/40 uppercase tracking-[0.3em]">La Bottega delle Voci</p>
        </div>
      </div>
    </div>
  );
};

export default MediaModal;
